/**
 * MASTER — delivery targets on the client, real measurement/rendering when the backend exists.
 *
 * WHAT IS ALWAYS REAL. The targets and characters. −14 LUFS with a −1 dBTP ceiling for
 * streaming, −16 for quieter social playback — those are stable delivery figures, and picking
 * one is a real mastering decision whether or not the backend is available in this session.
 *
 * WHAT BECOMES REAL IN BACKEND MODE. Measurement and rendering. When `audioEditApi.supports`
 * says mastering is available, this panel fetches the current track's measured LUFS/dBTP,
 * works out the delta to the selected target, and submits a real mastering request that
 * creates a new version.
 *
 * WHAT IS STILL DELIBERATELY ABSENT. A before/after switch, A-B compare and a local meter.
 * The backend renders a new version, but this panel does not fake audition controls of its
 * own; version comparison and playback stay in the workspace's existing transport/history
 * surfaces.
 *
 * See api/audioEditApi.ts — capability `master`, `getMasterAnalysis()` and `MasterRequest`,
 * which this panel's choices fill in exactly.
 */
import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Gauge } from 'lucide-react';
import { Btn, Chip, PrototypeNotice, Tooltip } from '../ui';
import DrawerHeader from './DrawerHeader';
import {
  AUDIO_EDIT_CAPABILITIES,
  audioEditApi,
  hasStoredAudioFile,
  needsStoredAudioFinalization,
  storedAudioRequirementMessage,
  type MasterAnalysis,
} from '../../api/audioEditApi';
import {
  MASTER_CHARACTERS,
  MASTER_TARGETS,
  MAX_LUFS,
  MIN_LUFS,
  clampLufs,
  formatLufs,
  gainToTarget,
  masterCharacter,
  masterTarget,
} from '../../lib/mastering';
import { c } from '../../styles/theme';
import type { Song, SongVersion } from '../../types/domain';

export interface MasterPanelProps {
  song: Song;
  processing: boolean;
  onBack: () => void;
  onClose: () => void;
  onMaster: (input: { target: string; targetLufs: number; truePeak: number; character: string }) => void;
  /** The version explicitly loaded (Version History's "Load") as the source for the next edit. */
  editingHeadVersion?: SongVersion | null;
}

const LABEL: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };
const DESC: CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 14px' };
const READOUT: CSSProperties = {
  fontSize: 11.5,
  fontFamily: 'IBM Plex Mono',
  color: c.text2,
  margin: '0 0 14px',
  minHeight: 16,
};

function formatSignedDb(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const abs = Math.abs(rounded);
  const whole = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  if (rounded > 0) return `+${whole} dB`;
  if (rounded < 0) return `−${whole} dB`;
  return '0 dB';
}

function formatDbtp(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const abs = Math.abs(rounded);
  const whole = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  return `${rounded < 0 ? '−' : ''}${whole} dBTP`;
}

function formatMeasuredLufs(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  const abs = Math.abs(rounded);
  const whole = Number.isInteger(abs) ? String(abs) : abs.toFixed(1);
  return `${rounded < 0 ? '−' : ''}${whole} LUFS`;
}

export default function MasterPanel({
  song,
  processing,
  onBack,
  onClose,
  onMaster,
  editingHeadVersion = null,
}: MasterPanelProps) {
  const [targetId, setTargetId] = useState(MASTER_TARGETS[0].id);
  const [characterId, setCharacterId] = useState(MASTER_CHARACTERS[0].id);
  const [customLufs, setCustomLufs] = useState(-14);
  const [analysis, setAnalysis] = useState<MasterAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [measuring, setMeasuring] = useState(false);

  const target = masterTarget(targetId);
  const character = masterCharacter(characterId);
  const custom = targetId === 'custom';
  const lufs = custom ? clampLufs(customLufs) : target.lufs;

  const capability = AUDIO_EDIT_CAPABILITIES.master;
  const canMaster = audioEditApi.supports('master');
  const storedAudioActionSource = editingHeadVersion ?? song;
  const storedAudioStatusSource = {
    songOrigin: song.songOrigin,
    audioUrl: storedAudioActionSource.audioUrl,
  };
  const storedAudioReady = hasStoredAudioFile(storedAudioActionSource);
  const finalizingAudio = !storedAudioReady && needsStoredAudioFinalization(storedAudioStatusSource);

  useEffect(() => {
    let cancelled = false;

    if (!canMaster || !storedAudioReady) {
      setAnalysis(null);
      setAnalysisError(null);
      setMeasuring(false);
      return () => {
        cancelled = true;
      };
    }

    setMeasuring(true);
    setAnalysisError(null);
    void audioEditApi
      .getMasterAnalysis(song.id, editingHeadVersion?.id ?? null)
      .then((result) => {
        if (cancelled) return;
        setAnalysis(result);
      })
      .catch((error) => {
        if (cancelled) return;
        setAnalysis(null);
        setAnalysisError(error instanceof Error ? error.message : 'Could not measure this song for mastering.');
      })
      .finally(() => {
        if (!cancelled) setMeasuring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canMaster, storedAudioReady, song.id, editingHeadVersion?.id]);

  const blockedByStoredAudio = canMaster && !storedAudioReady;
  const storedAudioMessage = storedAudioRequirementMessage(storedAudioStatusSource, 'master this song');
  const readout = !canMaster
    ? `${formatLufs(lufs)} integrated · ${target.truePeak} dBTP ceiling · ${target.description}`
    : blockedByStoredAudio
      ? `${finalizingAudio ? 'Finalizing audio…' : 'Stored audio required.'} Target ${formatLufs(lufs)} · ${target.truePeak} dBTP ceiling`
      : measuring
        ? `Measuring current loudness… target ${formatLufs(lufs)} · ${target.truePeak} dBTP ceiling`
        : analysis
          ? `Current ${formatMeasuredLufs(analysis.integratedLufs)} · ${formatDbtp(analysis.truePeakDbtp)} now · target ${formatLufs(lufs)} · ${target.truePeak} dBTP ceiling · ${formatSignedDb(gainToTarget(analysis.integratedLufs, lufs))}`
          : analysisError
            ? analysisError
            : `Target ${formatLufs(lufs)} · ${target.truePeak} dBTP ceiling`;

  const buttonLabel = processing ? 'Mastering track…' : measuring ? 'Analyzing...' : 'Master track';
  const button = (
    <Btn
      full
      disabled={processing || measuring || blockedByStoredAudio}
      onClick={
        !processing && !measuring && !blockedByStoredAudio
          ? () =>
              onMaster({
                target: targetId,
                targetLufs: lufs,
                truePeak: target.truePeak,
                character: characterId,
              })
          : undefined
      }
      aria-label={
        blockedByStoredAudio
          ? `Master track — ${finalizingAudio ? 'finalizing audio' : 'stored audio required'}`
          : measuring
            ? 'Analyzing...'
            : processing
              ? 'Mastering track…'
              : 'Master track'
      }
    >
      <Gauge size={13} />
      {buttonLabel}
    </Btn>
  );
  const buttonControl = blockedByStoredAudio ? (
    <Tooltip label={storedAudioMessage}>
      <span style={{ display: 'block', width: '100%' }}>{button}</span>
    </Tooltip>
  ) : (
    button
  );

  return (
    <div className="lyx-tool-panel">
      <DrawerHeader title="Master" onBack={onBack} onClose={onClose} />
      <p style={DESC}>
        Choose the delivery target for &quot;{song.title}&quot;.
        This panel measures the current track&apos;s LUFS and dBTP, then renders a mastered version.
      </p>

      {editingHeadVersion && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          Editing from {editingHeadVersion.label}. New mastering will branch from this loaded head until another version is explicitly loaded.
        </PrototypeNotice>
      )}

      {!canMaster && (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>
          Nothing has measured this song&apos;s loudness here yet. {capability.missing}
        </PrototypeNotice>
      )}

      {blockedByStoredAudio ? (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>{storedAudioMessage}</PrototypeNotice>
      ) : canMaster && analysisError ? (
        <PrototypeNotice style={{ marginTop: 0, marginBottom: 14 }}>{analysisError}</PrototypeNotice>
      ) : null}

      <p style={LABEL}>Target</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {MASTER_TARGETS.map((t) => (
          <Chip
            key={t.id}
            label={t.label}
            active={targetId === t.id}
            onClick={() => setTargetId(t.id)}
          />
        ))}
      </div>

      <p id="studio-master-readout" role="status" aria-live="polite" style={READOUT}>
        {readout}
      </p>

      {custom && (
        <div className="lyx-tool-row" style={{ marginBottom: 14 }}>
          <input
            id="studio-master-lufs"
            type="range"
            min={MIN_LUFS}
            max={MAX_LUFS}
            step={1}
            value={clampLufs(customLufs)}
            onChange={(e) => setCustomLufs(clampLufs(Number(e.target.value)))}
            aria-label="Custom loudness target in LUFS"
            aria-valuetext={formatLufs(clampLufs(customLufs))}
            className="lyx-tool-slider"
          />
          <span
            style={{
              fontSize: 12,
              fontFamily: 'IBM Plex Mono',
              color: c.text1,
              width: 78,
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            {formatLufs(clampLufs(customLufs))}
          </span>
        </div>
      )}

      <p style={LABEL}>Character</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {MASTER_CHARACTERS.map((ch) => (
          <Chip
            key={ch.id}
            label={ch.label}
            active={characterId === ch.id}
            onClick={() => setCharacterId(ch.id)}
          />
        ))}
      </div>
      <p style={{ fontSize: 11.5, color: c.text2, margin: '0 0 16px', minHeight: 16 }}>
        {character.description}
      </p>

      {buttonControl}
    </div>
  );
}
